import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as sdk from "@/generated/ui-api";
import i18n from "@/i18n";
import { UiBusinessEntityLookup } from "@/generated/ui-api/types.gen";

export const businessEntityKeys = {
  all: () => ["businessEntities", i18n.language] as const,
  current: () => [...businessEntityKeys.all(), "current"] as const,
  lookups: (lookups: UiBusinessEntityLookup[]) =>
    [...businessEntityKeys.all(), "lookups", { lookups }] as const,
  vatCheck: (vatNr: string) =>
    [...businessEntityKeys.all(), "vatCheck", { vatNr }] as const,
  updateEmailNotificationFlag: (sendEmailNotificationAboutReporting: boolean) =>
    [
      ...businessEntityKeys.all(),
      "updateEmailNotificationFlag",
      { sendEmailNotificationAboutReporting },
    ] as const,
};

export const useCheckVatValid = (vatNr: string, enabled: boolean = false) => {
  return useQuery({
    queryKey: businessEntityKeys.vatCheck(vatNr),
    queryFn: async () => {
      const response = await sdk.checkVatValid({
        query: { vatNr },
        throwOnError: true,
      });
      return response.data;
    },
    enabled: enabled && !!vatNr,
  });
};

export const useCurrentBusinessEntity = (enabled: boolean = true) => {
  return useQuery({
    queryKey: businessEntityKeys.current(),
    queryFn: async () => {
      const response = await sdk.getCurrentBusinessEntity({
        throwOnError: true,
      });
      return response.data;
    },
    enabled,
  });
};

export const useBusinessEntities = (lookups: UiBusinessEntityLookup[]) => {
  return useQuery({
    queryKey: businessEntityKeys.lookups(lookups),
    queryFn: async () => {
      const response = await sdk.findBusinessEntities({
        body: { lookups },
        throwOnError: true,
      });
      return response.data;
    },
    enabled: lookups.length > 0,
  });
};

export const useUpdateEmailNotificationFlag = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sendEmailNotificationAboutReporting: boolean) => {
      const response = await sdk.updateEmailNotificationFlag({
        query: { sendEmailNotificationAboutReporting },
        throwOnError: true,
      });

      return response.data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: businessEntityKeys.current(),
      });
    },
  });
};
